import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useMemo,
  useRef,
  useCallback
} from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import useDocumentScrollPagination from "../../hooks/useDocumentScrollPagination";
import useScrollPreload from "../../hooks/useScrollPreload";
import mergeTicketContact from "../../utils/mergeTicketContact";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { toast } from "react-toastify";
import { shouldDisplayFlowbuilderErrorToast } from "../../utils/flowbuilderErrorToast";
import { isPinnedForUser } from "../../utils/pinnedTickets";

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    minHeight: 0,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    borderBottomRightRadius: 0,
  },

  ticketsList: {
    flex: 1,
    minHeight: 0,
    height: "100%",
    maxHeight: "100%",
    overflowY: "auto",
    ...theme.neoUI.scrollbar.default,
    borderTop: "none",
    "html[data-phone-navigation] &": {
      overflowX: "hidden",
      overscrollBehaviorX: "none",
      touchAction: "pan-y pinch-zoom",
    },
    [theme.breakpoints.down("sm")]: {
      boxSizing: "border-box",
      paddingBottom: "var(--mobile-browser-bottom-extension, 0px)",
    },
  },

  ticketsListHeader: {
    color: "rgb(67, 83, 105)",
    zIndex: 2,
    backgroundColor: "transparent",
    borderBottom: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  ticketsCount: {
    fontWeight: "normal",
    color: "rgb(104, 121, 146)",
    marginLeft: "8px",
    fontSize: "14px",
  },

  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  const mergeTicketUser = (previousUser, nextTicket) => {
    if (!Object.prototype.hasOwnProperty.call(nextTicket, "user")) {
      return previousUser;
    }

    if (!nextTicket.user) {
      return nextTicket.user;
    }

    if (
      previousUser &&
      String(previousUser.id) === String(nextTicket.user.id)
    ) {
      return {
        ...previousUser,
        ...nextTicket.user,
        profileImage:
          nextTicket.user.profileImage !== undefined
            ? nextTicket.user.profileImage
            : previousUser.profileImage,
        isGhostUser:
          nextTicket.user.isGhostUser !== undefined
            ? nextTicket.user.isGhostUser
            : previousUser.isGhostUser
      };
    }

    return nextTicket.user;
  };

  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if ((ticket.unreadMessages || 0) > 0 || ticket.manualFlag) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = {
        ...state[ticketIndex],
        ...ticket,
        contact: ticket.contact || state[ticketIndex].contact,
        user: mergeTicketUser(state[ticketIndex].user, ticket),
        queue: ticket.queue || state[ticketIndex].queue,
        tags: Array.isArray(ticket.tags) ? ticket.tags : state[ticketIndex].tags,
        whatsapp: ticket.whatsapp || state[ticketIndex].whatsapp
      };
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = {
        ...state[ticketIndex],
        ...ticket,
        contact: ticket.contact || state[ticketIndex].contact,
        user: mergeTicketUser(state[ticketIndex].user, ticket),
        queue: ticket.queue || state[ticketIndex].queue,
        tags: Array.isArray(ticket.tags) ? ticket.tags : state[ticketIndex].tags,
        whatsapp: ticket.whatsapp || state[ticketIndex].whatsapp
      };
      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    return state.map(ticket => mergeTicketContact(ticket, contact));
  }

  if (action.type === "PATCH_TICKET") {
    const updatedTicket = action.payload;

    if (!updatedTicket || !updatedTicket.id) {
      return state;
    }

    const ticketIndex = state.findIndex(t => t.id === updatedTicket.id);
    if (ticketIndex === -1) {
      return [
        {
          ...updatedTicket,
          contact: updatedTicket.contact || null,
          tags: Array.isArray(updatedTicket.tags) ? updatedTicket.tags : [],
          user: updatedTicket.user || null,
          queue: updatedTicket.queue || null,
          whatsapp: updatedTicket.whatsapp || null
        },
        ...state
      ];
    }

    state[ticketIndex] = {
      ...state[ticketIndex],
      ...updatedTicket,
      contact: updatedTicket.contact || state[ticketIndex].contact,
      user: mergeTicketUser(state[ticketIndex].user, updatedTicket),
      tags: Array.isArray(updatedTicket.tags) ? updatedTicket.tags : state[ticketIndex].tags
    };

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_TASKS") {
    const { ticketId, task, action: taskAction } = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);

    if (ticketIndex !== -1) {
      const currentTicket = state[ticketIndex];
      const currentTasks = Array.isArray(currentTicket.tasks) ? currentTicket.tasks : [];

      let nextTasks = [...currentTasks];

      if (taskAction === "create" || taskAction === "update") {
        const taskIndex = nextTasks.findIndex((t) => t.id === task.id);
        if (taskIndex !== -1) {
          nextTasks[taskIndex] = task;
        } else {
          nextTasks.push(task);
        }
      } else if (taskAction === "delete") {
        nextTasks = nextTasks.filter((t) => t.id !== task.id);
      }

      state[ticketIndex] = {
        ...currentTicket,
        tasks: nextTasks
      };
    }

    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const arraysShallowEqual = (a = [], b = []) => {
  if (a === b) {
    return true;
  }

  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
};

const RECENT_ITEM_WINDOW = 2000;

const TicketsListCustom = (props) => {
  const {
    status,
    searchParam,
    tags,
    users,
    showAll,
    selectedQueueIds,
    updateCount,
    style,
    prioritizeUnread,
  } = props;
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [targetPage, setTargetPage] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const ticketsListRef = useRef([]);
  const recentlyAddedRef = useRef(new Map());
  const { user } = useContext(AuthContext);
  const {
    profile,
    queues = [],
    allowNoQueue,
    id: userId,
  } = user;
  const normalizedSearchParam =
    typeof searchParam === "string" ? searchParam.trim() : "";
  const isSearching = normalizedSearchParam.length > 0;
  const orderedTicketsList = useMemo(() => {
    if (!status || isSearching) {
      return ticketsList;
    }

    const pinned = [];
    const others = [];

    ticketsList.forEach((ticket) => {
      const pinnedByUser = isPinnedForUser(ticket.isPinned, status, userId);

      if (pinnedByUser) {
        pinned.push(ticket);
      } else {
        others.push(ticket);
      }
    });

    // Aplicar priorização de não lidos aos não fixados
    let sortedOthers = others;
    if (prioritizeUnread) {
      sortedOthers = [...others].sort((a, b) => {
        const unreadA = a.unreadMessages || 0;
        const unreadB = b.unreadMessages || 0;
        const highlightedA = unreadA > 0 || Boolean(a.manualFlag);
        const highlightedB = unreadB > 0 || Boolean(b.manualFlag);

        // Tickets com não lidos ou manualFlag vêm primeiro
        if (highlightedA && !highlightedB) return -1;
        if (!highlightedA && highlightedB) return 1;

        // Entre tickets com não lidos, ordenar por quantidade (mais não lidos primeiro)
        if (unreadA > 0 && unreadB > 0) {
          return unreadB - unreadA;
        }

        // Para tickets sem não lidos, manter ordem padrão
        return 0;
      });
    }

    return [...pinned, ...sortedOthers];
  }, [ticketsList, userId, status, isSearching, prioritizeUnread]);
  const normalizedQueueIds = useMemo(
    () => (Array.isArray(selectedQueueIds) ? selectedQueueIds : []),
    [selectedQueueIds]
  );
  const normalizedTagIds = useMemo(
    () => (Array.isArray(tags) ? tags : []),
    [tags]
  );
  const normalizedUserIds = useMemo(
    () => (Array.isArray(users) ? users : []),
    [users]
  );

  useEffect(() => {
    ticketsListRef.current = ticketsList;
  }, [ticketsList]);

  const [refreshToken, setRefreshToken] = useState(0);

  const filtersSnapshotRef = useRef({
    status,
    searchParam,
    showAll,
    tagIds: normalizedTagIds,
    userIds: normalizedUserIds,
    queueIds: normalizedQueueIds,
  });

  const socketManager = useContext(SocketContext);

  const markTicketAsRecentlyAdded = useCallback((ticketId) => {
    if (!ticketId) {
      return;
    }

    const now = Date.now();
    const map = recentlyAddedRef.current;
    map.set(ticketId, now);

    map.forEach((addedAt, id) => {
      if (now - addedAt > RECENT_ITEM_WINDOW) {
        map.delete(id);
      }
    });
  }, []);

  const clearRecentAddition = useCallback((ticketId) => {
    if (!ticketId) {
      return;
    }

    recentlyAddedRef.current.delete(ticketId);
  }, []);

  useEffect(() => {
    return () => {
      recentlyAddedRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const prev = filtersSnapshotRef.current;

    const showAllChanged = prev.showAll !== showAll;
    const statusChanged = prev.status !== status;
    const searchChanged = prev.searchParam !== searchParam;
    const tagFilterChanged = !arraysShallowEqual(prev.tagIds, normalizedTagIds);
    const userFilterChanged = !arraysShallowEqual(prev.userIds, normalizedUserIds);
    const queueFilterChanged = !arraysShallowEqual(prev.queueIds, normalizedQueueIds);

    if (
      !showAllChanged &&
      !statusChanged &&
      !searchChanged &&
      !tagFilterChanged &&
      !userFilterChanged &&
      !queueFilterChanged
    ) {
      return;
    }

    filtersSnapshotRef.current = {
      status,
      searchParam,
      showAll,
      tagIds: normalizedTagIds,
      userIds: normalizedUserIds,
      queueIds: normalizedQueueIds,
    };

    dispatch({ type: "RESET" });
    setPageNumber(1);

    if (!showAllChanged || statusChanged || searchChanged || tagFilterChanged || userFilterChanged || queueFilterChanged) {
      setTargetPage(1);
    }
  }, [status, searchParam, showAll, normalizedTagIds, normalizedUserIds, normalizedQueueIds]);

  useEffect(() => {
    const handlePatchedTicketEvent = (event) => {
      const updatedTicket = event.detail?.ticket;
      if (!updatedTicket?.id) {
        return;
      }

      const wasAlreadyListed = ticketsListRef.current.some(
        (t) => t.id === updatedTicket.id
      );
      const statusMatches = !status || updatedTicket.status === status;

      if (!statusMatches) {
        if (wasAlreadyListed) {
          clearRecentAddition(updatedTicket.id);
          dispatch({ type: "DELETE_TICKET", payload: updatedTicket.id });
        }
        return;
      }

      dispatch({ type: "PATCH_TICKET", payload: updatedTicket });
      if (!wasAlreadyListed) {
        markTicketAsRecentlyAdded(updatedTicket.id);
      }
    };

    window.addEventListener("ticket:patched", handlePatchedTicketEvent);

    return () => {
      window.removeEventListener("ticket:patched", handlePatchedTicketEvent);
    };
  }, [status, markTicketAsRecentlyAdded, clearRecentAddition]);

  const { tickets, hasMore, loading, count, avatarRefreshEnabled } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    tags: JSON.stringify(normalizedTagIds),
    users: JSON.stringify(normalizedUserIds),
    queueIds: JSON.stringify(normalizedQueueIds),
    refreshToken,
    prefetchNextPage: style?.display !== "none",
  });

  useEffect(() => {
    if (loading) {
      return;
    }

    if (pageNumber >= targetPage) {
      return;
    }

    if (!hasMore) {
      setTargetPage((prev) => (prev > pageNumber ? pageNumber : prev));
      return;
    }

    setPageNumber((prev) => prev + 1);
  }, [loading, pageNumber, targetPage, hasMore]);

  useEffect(() => {
    if (!isSearching) {
      return;
    }

    if (loading) {
      return;
    }

    if (!hasMore) {
      return;
    }

    if (targetPage > pageNumber) {
      return;
    }

    setTargetPage((prev) => prev + 1);
  }, [isSearching, loading, hasMore, targetPage, pageNumber]);

  useEffect(() => {
    const allowNoQueueEnabled = Boolean(allowNoQueue);
    const queueIds = queues.map((q) => q.id);
    const hasUserFilter = normalizedUserIds.length > 0;
    const queueFilterActive = normalizedQueueIds.length > 0;
    const hasNoQueueFilter = normalizedQueueIds.indexOf(0) > -1;
    const hasTagFilter = normalizedTagIds.length > 0;
    const userFilterSet = new Set(normalizedUserIds);
    const tagFilterSet = new Set(normalizedTagIds);

    const matchesTagFilter = (ticket) => {
      if (!hasTagFilter) {
        return true;
      }

      if (!Array.isArray(ticket.tags)) {
        return false;
      }

      return ticket.tags.some((tag) => tagFilterSet.has(tag.id));
    };

    if (showAll) {
      const ticketsFilteredByTags = hasTagFilter ? tickets.filter(matchesTagFilter) : tickets;
      // Não aplicar filtro de usuário para tickets pendentes, pois eles não têm usuário atribuído
      const shouldApplyUserFilter = hasUserFilter && status !== "pending";
      const ticketsFilteredByUsers = shouldApplyUserFilter
        ? ticketsFilteredByTags.filter((t) => Boolean(t.isShared) || userFilterSet.has(t.userId))
        : ticketsFilteredByTags;
      const ticketsFilteredByQueues = queueFilterActive
        ? ticketsFilteredByUsers.filter((t) => {
          const isTicketWithoutQueue = t.queueId === null || t.queueId === undefined;
          if (isTicketWithoutQueue) {
            return hasNoQueueFilter;
          }
          return normalizedQueueIds.indexOf(t.queueId) > -1;
        })
        : ticketsFilteredByUsers;

      dispatch({ type: "LOAD_TICKETS", payload: ticketsFilteredByQueues });
      return;
    }

    const filteredTickets = tickets.filter((t) => {
      if (t.userId && t.userId !== userId) {
        return false;
      }

      if (!matchesTagFilter(t)) {
        return false;
      }

      // Não aplicar filtro de usuário para tickets pendentes
      if (hasUserFilter && status !== "pending") {
        return Boolean(t.isShared) || userFilterSet.has(t.userId);
      }

      const isTicketWithoutQueue = t.queueId === null || t.queueId === undefined;
      if (queueFilterActive) {
        if (isTicketWithoutQueue) {
          if (!hasNoQueueFilter) {
            return false;
          }
          return allowNoQueueEnabled;
        }

        return normalizedQueueIds.indexOf(t.queueId) > -1;
      }

      if (isTicketWithoutQueue) {
        return allowNoQueueEnabled;
      }

      return queueIds.indexOf(t.queueId) > -1;
    });

    dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
  }, [tickets, queues, showAll, allowNoQueue, userId, normalizedUserIds, normalizedQueueIds, normalizedTagIds]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const allowNoQueueEnabled = Boolean(allowNoQueue);
    const userFilterSet = new Set(normalizedUserIds);
    const hasUserFilter = userFilterSet.size > 0;
    const hasTagFilter = normalizedTagIds.length > 0;
    const tagFilterSet = new Set(normalizedTagIds);
    const queueFilterActive = normalizedQueueIds.length > 0;
    const hasNoQueueFilter = normalizedQueueIds.indexOf(0) > -1;

    const matchesTagFilter = (ticket) => {
      if (!hasTagFilter) {
        return true;
      }

      const ticketTags = Array.isArray(ticket.tags) ? ticket.tags : [];
      return ticketTags.some((tag) => tagFilterSet.has(tag.id));
    };

    const shouldUpdateTicket = (ticket) => {
      if (!matchesTagFilter(ticket)) {
        return false;
      }

      // Se for um ticket compartilhado ("Todos"), sempre permite
      if (ticket.isShared) {
        return true;
      }

      if (!showAll && ticket.userId && ticket.userId !== userId) {
        return false;
      }

      const isTicketOwner = ticket.userId === userId;
      // Não aplicar filtro de usuário para tickets pendentes
      const shouldApplyUserFilter = hasUserFilter && ticket.status !== "pending";
      const userMatchesFilter = shouldApplyUserFilter ? userFilterSet.has(ticket.userId) : true;
      const ticketWithoutQueue = ticket.queueId === null || ticket.queueId === undefined;

      if (showAll) {
        if (shouldApplyUserFilter && !userMatchesFilter) {
          return false;
        }

        if (!queueFilterActive) {
          return true;
        }

        if (ticketWithoutQueue) {
          return hasNoQueueFilter;
        }

        return normalizedQueueIds.indexOf(ticket.queueId) > -1;
      }

      // Verifica permissões de usuário
      const hasUserPermission =
        !ticket.userId ||
        isTicketOwner ||
        (hasUserFilter && userMatchesFilter);

      // Se não tem permissão de usuário, não atualiza
      if (!hasUserPermission) return false;

      if (shouldApplyUserFilter && !userMatchesFilter) {
        return false;
      }

      if (!queueFilterActive) {
        if (ticketWithoutQueue) {
          return allowNoQueueEnabled || isTicketOwner;
        }
        return true;
      }

      if (ticketWithoutQueue) {
        if (!hasNoQueueFilter) {
          return false;
        }
        return allowNoQueueEnabled || isTicketOwner;
      }

      // Se o ticket tem fila, verifica se está na lista selecionada
      return normalizedQueueIds.indexOf(ticket.queueId) > -1;
    };

    const notBelongsToUserQueues = (ticket) => {
      if (ticket.isShared) {
        return false;
      }

      if (!matchesTagFilter(ticket)) {
        return true;
      }

      if (!showAll && ticket.userId && ticket.userId !== userId) {
        return true;
      }

      const isTicketOwner = ticket.userId === userId;
      // Não aplicar filtro de usuário para tickets pendentes
      const shouldApplyUserFilter = hasUserFilter && ticket.status !== "pending";
      const userMatchesFilter = shouldApplyUserFilter ? userFilterSet.has(ticket.userId) : true;
      const ticketWithoutQueue = ticket.queueId === null || ticket.queueId === undefined;

      if (showAll) {
        if (shouldApplyUserFilter && !userMatchesFilter) {
          return true;
        }

        if (!queueFilterActive) {
          return false;
        }

        if (ticketWithoutQueue) {
          return !hasNoQueueFilter;
        }

        return normalizedQueueIds.indexOf(ticket.queueId) === -1;
      }

      if (shouldApplyUserFilter && !userMatchesFilter) {
        return true;
      }

      // Sem filtros: remove tickets sem fila se usuário não tem permissão
      if (!queueFilterActive) {
        if (ticketWithoutQueue && !allowNoQueueEnabled && !isTicketOwner) {
          return true;
        }
        return false;
      }

      // Se o ticket não tem fila e "Sem Fila" NÃO está selecionado ou usuário sem permissão
      if (ticketWithoutQueue) {
        if (!allowNoQueueEnabled && !isTicketOwner) {
          return true;
        }
        return !hasNoQueueFilter;
      }

      // Se o ticket tem fila e essa fila não está selecionada
      return normalizedQueueIds.indexOf(ticket.queueId) === -1;
    };

    socket.on("ready", () => {
      if (status) {
        socket.emit("joinTickets", status);
      } else {
        socket.emit("joinNotification");
      }
    });

    socket.on(`company-${companyId}-ticket`, (data) => {
      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
      }

      if (data.action === "flowbuilderError") {
        if (shouldDisplayFlowbuilderErrorToast(data.eventId)) {
          toast.error(
            data.message ||
            "Não foi possível transferir: já existe outro ticket nessa conexão."
          );
        }
      }

      if (data.action === "update") {
        if (data.ticket.status !== status) {
          const isCurrentlyListed = ticketsListRef.current.some((t) => t.id === data.ticket.id);
          if (isCurrentlyListed) {
            clearRecentAddition(data.ticket.id);
            dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
            setTargetPage(1);
            setPageNumber(1);
            setRefreshToken((prev) => prev + 1);
          }
          return;
        }

        if (shouldUpdateTicket(data.ticket)) {
          const wasAlreadyListed = ticketsListRef.current.some((t) => t.id === data.ticket.id);
          dispatch({
            type: "UPDATE_TICKET",
            payload: data.ticket,
          });
          if (!wasAlreadyListed) {
            markTicketAsRecentlyAdded(data.ticket.id);
            setTargetPage(1);
            setPageNumber(1);
            setRefreshToken((prev) => prev + 1);
          }
        }
        if (notBelongsToUserQueues(data.ticket)) {
          const isCurrentlyListed = ticketsListRef.current.some((t) => t.id === data.ticket.id);
          if (isCurrentlyListed) {
            clearRecentAddition(data.ticket.id);
            dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
            setTargetPage(1);
            setPageNumber(1);
            setRefreshToken((prev) => prev + 1);
          }
        }
        return;
      }

      if (data.action === "delete") {
        const addedAt = recentlyAddedRef.current.get(data.ticketId);
        if (addedAt && Date.now() - addedAt < RECENT_ITEM_WINDOW) {
          recentlyAddedRef.current.delete(data.ticketId);
          return;
        }
        clearRecentAddition(data.ticketId);
        const isCurrentlyListed = ticketsListRef.current.some((t) => t.id === data.ticketId);
        if (isCurrentlyListed) {
          dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
          setTargetPage(1);
          setPageNumber(1);
          setRefreshToken((prev) => prev + 1);
        }
      }
    });

    socket.on(`company-${companyId}-task`, (data) => {
      if (!data) return;
      const { action, task, ticketId } = data;
      const targetTicketId = ticketId || task?.ticketId;

      if (!targetTicketId) return;

      dispatch({
        type: "UPDATE_TICKET_TASKS",
        payload: {
          ticketId: targetTicketId,
          task: task || { id: data.taskId },
          action
        }
      });
    });

    socket.on(`company-${companyId}-appMessage`, (data) => {
      const queueIds = queues.map((q) => q.id);
      if (profile === "user") {
        // Bloqueia apenas se tem fila E não está nas filas do usuário
        if (data.ticket.queueId && queueIds.indexOf(data.ticket.queueId) === -1) {
          return;
        }
        // Bloqueia se não tem fila MAS tem responsável E não é o responsável
        if (!data.ticket.queueId && data.ticket.userId && data.ticket.userId !== user.id) {
          return;
        }
        // Permite: tickets sem fila e sem responsável
      }

      if (data.action === "create" && shouldUpdateTicket(data.ticket) && (status === undefined || data.ticket.status === status)) {
        const wasAlreadyListed = ticketsListRef.current.some((t) => t.id === data.ticket.id);
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: data.ticket,
        });
        if (!wasAlreadyListed) {
          markTicketAsRecentlyAdded(data.ticket.id);
          setTargetPage(1);
          setPageNumber(1);
          setRefreshToken((prev) => prev + 1);
        }
      }

      if (data.action === "update" && shouldUpdateTicket(data.ticket) && (status === undefined || data.ticket.status === status)) {
        const wasAlreadyListed = ticketsListRef.current.some((t) => t.id === data.ticket.id);
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: data.ticket,
        });
        if (!wasAlreadyListed) {
          markTicketAsRecentlyAdded(data.ticket.id);
          setTargetPage(1);
          setPageNumber(1);
          setRefreshToken((prev) => prev + 1);
        }
      }
    });

    socket.on(`company-${companyId}-task`, (data) => {
      if (data.action === "update" || data.action === "create") {
        const task = data.task;
        const ticketId = task.ticketId;
        if (!ticketId) return;

        const ticket = ticketsListRef.current.find((t) => t.id === ticketId);
        if (ticket) {
          const newTasks = [...(ticket.tasks || [])];
          const taskIndex = newTasks.findIndex((t) => t.id === task.id);
          if (taskIndex !== -1) {
            newTasks[taskIndex] = task;
          } else {
            newTasks.push(task);
          }
          dispatch({
            type: "UPDATE_TICKET",
            payload: { ...ticket, tasks: newTasks },
          });
        }
      }

      if (data.action === "delete") {
        const { taskId, ticketId } = data;
        if (!ticketId) return;

        const ticket = ticketsListRef.current.find((t) => t.id === ticketId);
        if (ticket) {
          const newTasks = (ticket.tasks || []).filter((t) => t.id !== taskId);
          dispatch({
            type: "UPDATE_TICKET",
            payload: { ...ticket, tasks: newTasks },
          });
        }
      }
    });

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [
    status,
    showAll,
    userId,
    normalizedQueueIds,
    normalizedTagIds,
    normalizedUserIds,
    allowNoQueue,
    queues,
    profile,
    markTicketAsRecentlyAdded,
    clearRecentAddition,
    socketManager,
  ]);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(count);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const loadMore = () => {
    setTargetPage((prevState) => prevState + 1);
  };

  const preloadEnabled = hasMore && !loading && targetPage === pageNumber;

  const handleScroll = useScrollPreload({
    enabled: preloadEnabled,
    loading,
    onLoadMore: loadMore,
  });

  const documentListRef = useDocumentScrollPagination({
    enabled: preloadEnabled,
    loading,
    onLoadMore: loadMore,
  });

  return (
    <Paper square elevation={0} className={classes.ticketsListWrapper} style={style}>
      <Paper
        square
        name="closed"
        ref={documentListRef}
        data-mobile-document-list
        elevation={0}
        className={`${classes.ticketsList} scrollable-container`}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t("ticketsList.noTicketsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("ticketsList.noTicketsMessage")}
              </p>
            </div>
          ) : (
            <>
              {orderedTicketsList.map((ticket) => (
                <TicketListItem ticket={ticket} key={ticket.id} status={status} avatarRefreshEnabled={avatarRefreshEnabled} />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsListCustom;
